import { useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { CandidateApprovePayload, VenueCandidateRecord } from "@/api/venueIngestion"

const formatReasonValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return ""
  }
  if (typeof value === "string" || typeof value === "number") {
    return value.toString()
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(", ")
  }
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${key}: ${formatReasonValue(item)}`)
      .join(" · ")
  }
  return String(value)
}

type VenueCandidateReviewProps = {
  candidate: VenueCandidateRecord
  onApprove: (id: string, payload: CandidateApprovePayload) => Promise<void>
  onReject: (id: string, reason: string) => Promise<void>
  checked: boolean
  onToggleSelect: (id: string, value: boolean) => void
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive"> = {
  pending: "secondary",
  approved: "default",
  enriching: "default",
  rejected: "destructive",
}

export default function VenueCandidateReview({
  candidate,
  onApprove,
  onReject,
  checked,
  onToggleSelect,
}: VenueCandidateReviewProps) {
  const [form, setForm] = useState({
    venue_name: candidate.venue_name,
    city: candidate.city || "",
    state: candidate.state || "",
    website: candidate.website || "",
    booking_email: candidate.booking_email || "",
    phone: candidate.phone || "",
    notes: candidate.notes || "",
  })
  const [approving, setApproving] = useState(false)
  const [rejecting, setRejecting] = useState(false)
  const [dismissing, setDismissing] = useState(false)
  const [showReject, setShowReject] = useState(false)
  const [rejectReason, setRejectReason] = useState("")

  const scoreValue = candidate.confidence_score ?? candidate.confidence ?? 0
  const scoreLevel = scoreValue >= 0.75 ? "High" : scoreValue >= 0.45 ? "Medium" : "Low"
  const statusBadge = STATUS_VARIANTS[candidate.status] || "secondary"
  const confidenceLabelValue = candidate.confidence_score ?? candidate.confidence
  const confidenceLabel = confidenceLabelValue != null ? `${(confidenceLabelValue * 100).toFixed(0)}%` : "Unknown"
  const snippet = candidate.source_data?.snippet ?? candidate.enrichment_data?.google?.formatted_address ?? ""

  const handleApprove = async () => {
    setApproving(true)
    try {
      await onApprove(candidate.id, {
        venue_name: form.venue_name,
        city: form.city || undefined,
        state: form.state || undefined,
        website: form.website || undefined,
        booking_email: form.booking_email || undefined,
        phone: form.phone || undefined,
        notes: form.notes || undefined,
      })
    } finally {
      setApproving(false)
    }
  }

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      return
    }
    setRejecting(true)
    try {
      await onReject(candidate.id, rejectReason.trim())
      setRejectReason("")
      setShowReject(false)
    } finally {
      setRejecting(false)
    }
  }

  const handleDismiss = async () => {
    if (candidate.status !== "pending") {
      return
    }
    setDismissing(true)
    try {
      await onReject(candidate.id, "Dismissed by reviewer")
    } finally {
      setDismissing(false)
    }
  }

  return (
    <details className="w-full rounded border border-muted-foreground/20 bg-background text-sm group">
      <summary className="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(event) => onToggleSelect(candidate.id, event.target.checked)}
          className="h-4 w-4 rounded border border-muted-foreground text-foreground"
        />
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{candidate.venue_name || "Unnamed venue"}</span>
          <span className="text-xs text-muted-foreground">
            {candidate.city || "Unknown city"}, {candidate.state || "??"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={statusBadge}>{candidate.status}</Badge>
          <Badge variant="outline">{confidenceLabel} confidence</Badge>
          {candidate.status === "pending" ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                void handleDismiss()
              }}
              disabled={dismissing}
            >
              {dismissing ? "Dismissing…" : "Dismiss"}
            </Button>
          ) : null}
        </div>
      </summary>
      <div className="px-4 pb-4">
        <Card className="space-y-4 border-muted-foreground/20">
          <CardHeader className="flex items-center justify-between gap-4">
            <div>
              <CardTitle>{candidate.venue_name || "Venue details"}</CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{candidate.source_type.replace("_", " ")}</span>
                <Badge variant="outline">{confidenceLabel} confidence</Badge>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 text-xs text-muted-foreground">
              <Badge variant={statusBadge}>{candidate.status}</Badge>
              <span>
                Confidence {scoreValue.toFixed(2)} ({scoreLevel})
              </span>
            </div>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor={`venue-name-${candidate.id}`}>Venue name</Label>
              <Input
                id={`venue-name-${candidate.id}`}
                value={form.venue_name}
                onChange={(event) => setForm((state) => ({ ...state, venue_name: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`city-${candidate.id}`}>City</Label>
              <Input
                id={`city-${candidate.id}`}
                value={form.city}
                onChange={(event) => setForm((state) => ({ ...state, city: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`state-${candidate.id}`}>State</Label>
              <Input
                id={`state-${candidate.id}`}
                value={form.state}
                onChange={(event) => setForm((state) => ({ ...state, state: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`booking-${candidate.id}`}>Booking email</Label>
              <Input
                id={`booking-${candidate.id}`}
                value={form.booking_email}
                onChange={(event) => setForm((state) => ({ ...state, booking_email: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`phone-${candidate.id}`}>Phone</Label>
              <Input
                id={`phone-${candidate.id}`}
                value={form.phone}
                onChange={(event) => setForm((state) => ({ ...state, phone: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`website-${candidate.id}`}>Website</Label>
              <Input
                id={`website-${candidate.id}`}
                value={form.website}
                onChange={(event) => setForm((state) => ({ ...state, website: event.target.value }))}
              />
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label htmlFor={`notes-${candidate.id}`}>Notes</Label>
              <Textarea
                id={`notes-${candidate.id}`}
                value={form.notes}
                onChange={(event) => setForm((state) => ({ ...state, notes: event.target.value }))}
                rows={3}
              />
            </div>
          </CardContent>
          <CardContent className="space-y-1 text-sm text-muted-foreground">
            {snippet ? <p className="whitespace-pre-line">{snippet}</p> : <p>No source snippet available</p>}
            <div className="flex flex-wrap gap-2">
              {candidate.genres?.map((genre) => (
                <Badge key={`${candidate.id}-${genre}`} variant="outline">
                  {genre}
                </Badge>
              ))}
            </div>
          </CardContent>
          <CardFooter className="flex flex-wrap gap-2">
            <Button onClick={handleApprove} disabled={approving} size="sm">
              {approving ? "Approving…" : "Approve"}
            </Button>
            <Button
              variant="destructive"
              onClick={() => setShowReject((state) => !state)}
              disabled={rejecting}
              size="sm"
            >
              Reject
            </Button>
          </CardFooter>
          {candidate.confidence_reasons && (
            <CardContent className="border-t border-muted-foreground/20">
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium text-muted-foreground">
                  Confidence details
                </summary>
                <div className="mt-2 space-y-2">
                  {Object.entries(candidate.confidence_reasons).map(([key, value]) => (
                    <div key={key} className="flex flex-wrap items-center gap-2 text-[0.75rem]">
                      <Badge variant="outline" className="text-[0.6rem]">
                        {key.replace(/_/g, " ")}
                      </Badge>
                      <span className="text-muted-foreground">{formatReasonValue(value)}</span>
                    </div>
                  ))}
                </div>
              </details>
            </CardContent>
          )}
          {showReject && (
            <CardContent className="space-y-2 border-t border-muted-foreground/20">
              <Label htmlFor={`reject-reason-${candidate.id}`}>Rejection reason</Label>
              <Textarea
                id={`reject-reason-${candidate.id}`}
                value={rejectReason}
                onChange={(event) => setRejectReason(event.target.value)}
                rows={2}
              />
              <Button onClick={handleReject} disabled={rejecting} size="sm" variant="destructive">
                {rejecting ? "Rejecting…" : "Confirm reject"}
              </Button>
            </CardContent>
          )}
        </Card>
      </div>
    </details>
  )
}
